import { Modal, Toast } from "@monsterindia/global-ui-components";
import React, { useEffect } from "react";

import { ExternalApopType, ToastType } from "../../types/apop";
import { BasicDetailsApop } from "./basicDetails";
import { EducationDetailsApop } from "./educationDetails";
import { EmploymentApop } from "./employment";
import { JobPreferencesApop } from "./jobPreferences";
import { LgbtqiaApop } from "./lgbtqia";
import { ResumeEmploymentApop } from "./resumeEmployment";
import { SalaryNudge } from "./salary";
import { VerificationApop } from "./verification";

const ApopComponent = ({
  type,
  openApop,
  closeApop,
  onCTA,
  data,
  isRecaptchaEnabled,
  showEmailVerification,
  onApopComplete,
  flowType = "L1_APOP",
  jobData,
  handleEventTracking = (v) => {},
  eventName,
  callOnCompleteBeforeClose,
}: ExternalApopType) => {
  const [apopType, setApopType] = React.useState(type);
  const [prefilledData, setPrefilledData] = React.useState(data);
  const [prefillJobData, setPrefillJobData] = React.useState(jobData);
  const [openToast, setOpenToast] = React.useState({
    showToast: false,
    text: "",
    type: "",
  });

  useEffect(() => {
    if (type) {
      setApopType(type);
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Profile Nudge Application Flow Shown",
        eventProps: { profileNudgeType: apopType },
      });
    }
  }, [type]);

  useEffect(() => {
    if (data && typeof data === "object") {
      setPrefilledData({ ...data });
    }
  }, [data]);

  const nextApoptype = (
    nextType: string | undefined,
    data?: any,
    job?: any,
  ) => {
    if (!nextType) {
      if (callOnCompleteBeforeClose) {
        // Special case for Bulkapply → if onApopComplete exist then complete first, then close
        Promise.resolve(onApopComplete && onApopComplete())
          .then(() => {
            closeApop();
          })
          .catch((error) => {
            console.error("Error in onApopComplete:", error);
            closeApop();
          });
      } else {
        closeApop();
        onApopComplete && onApopComplete();
      }
      return;
    }
    // If job data is not present, set it to null
    if (!job) {
      setPrefillJobData(undefined);
    }
    setApopType(nextType);
    setPrefilledData(data);
  };

  const toastHandler = ({ showToast, text, type }: ToastType) => {
    setOpenToast({
      showToast,
      text,
      type,
    });
  };

  let apopComponent;

  switch (apopType) {
    case "BASIC_DETAILS":
      apopComponent = (
        <BasicDetailsApop
          prefilledData={prefilledData}
          nextApopType={nextApoptype}
          onCTA={onCTA}
          toast={toastHandler}
          closeApop={closeApop}
          flowType={flowType}
          jobData={jobData}
          handleEventTracking={handleEventTracking}
          eventName={eventName}
        />
      );
      break;

    case "EDUCATION":
      apopComponent = (
        <EducationDetailsApop
          prefilledData={prefilledData}
          nextApopType={nextApoptype}
          onCTA={onCTA}
          toast={toastHandler}
          closeApop={closeApop}
          flowType={flowType}
          jobData={jobData}
          handleEventTracking={handleEventTracking}
          eventName={eventName}
        />
      );
      break;

    case "EMPLOYMENT":
      apopComponent = (
        <EmploymentApop
          prefilledData={prefilledData}
          nextApopType={nextApoptype}
          onCTA={onCTA}
          toast={toastHandler}
          closeApop={closeApop}
          flowType={flowType}
          jobData={jobData}
          handleEventTracking={handleEventTracking}
          eventName={eventName}
        />
      );
      break;

    case "JOB_PREFERENCE":
      apopComponent = (
        <JobPreferencesApop
          prefilledData={prefilledData}
          nextApopType={nextApoptype}
          onCTA={onCTA}
          toast={toastHandler}
          closeApop={closeApop}
          flowType={flowType}
          jobData={jobData}
          handleEventTracking={handleEventTracking}
          eventName={eventName}
        />
      );
      break;

    case "SALARY":
      apopComponent = (
        <SalaryNudge
          prefilledData={prefilledData}
          isL1Apop={true}
          nextApopType={nextApoptype}
          onCTA={onCTA}
          toast={toastHandler}
          closeApop={closeApop}
          onApopComplete={onApopComplete}
          flowType={flowType}
          handleEventTracking={handleEventTracking}
          eventName={eventName}
          callOnCompleteBeforeClose={callOnCompleteBeforeClose}
        />
      );
      break;

    case "UPDATE_RESUME_AND_EMPLOYMENT":
      apopComponent = (
        <ResumeEmploymentApop
          prefilledData={prefilledData}
          nextApopType={nextApoptype}
          onCTA={onCTA}
          toast={toastHandler}
          closeApop={closeApop}
          flowType={"PROFILE_NUDGE"}
          jobData={prefillJobData}
          handleEventTracking={handleEventTracking}
          eventName={eventName}
        />
      );
      break;

    case "UPDATE_WORK_AUTH":
      apopComponent = (
        <BasicDetailsApop
          prefilledData={prefilledData}
          nextApopType={nextApoptype}
          onCTA={onCTA}
          toast={toastHandler}
          closeApop={closeApop}
          flowType={"PROFILE_NUDGE"}
          jobData={prefillJobData}
          handleEventTracking={handleEventTracking}
          eventName={eventName}
        />
      );
      break;

    case "VERIFICATION":
      apopComponent = (
        <VerificationApop
          prefilledData={prefilledData}
          type="VERIFICATION"
          nextApopType={nextApoptype}
          isRecaptchaEnabled={isRecaptchaEnabled}
          showEmailVerification={showEmailVerification}
          onCTA={onCTA}
          toast={toastHandler}
          closeApop={closeApop}
          flowType={flowType}
          jobData={jobData}
          handleEventTracking={handleEventTracking}
          eventName={eventName}
        />
      );
      break;

    case "EVENT":
      apopComponent = (
        <LgbtqiaApop
          prefilledData={prefilledData}
          closeApop={closeApop}
          nextApopType={nextApoptype}
          toast={toastHandler}
          eventName={eventName}
          flowType={flowType}
          handleEventTracking={handleEventTracking}
        />
      );
      break;

    default:
      apopComponent = (
        <VerificationApop
          prefilledData={prefilledData}
          type="VERIFICATION"
          nextApopType={nextApoptype}
          isRecaptchaEnabled={isRecaptchaEnabled}
          showEmailVerification={showEmailVerification}
          onCTA={onCTA}
          toast={toastHandler}
          closeApop={closeApop}
          flowType={flowType}
          jobData={jobData}
          handleEventTracking={handleEventTracking}
          eventName={eventName}
        />
      );
      break;
  }

  return (
    <Modal
      showModal={openApop}
      closeModal={() => closeApop()}
      isMobile={window.innerWidth < 768}
      showCloseButton={window.innerWidth < 768 ? false : true}
      screenAlignment={window.innerWidth < 768 ? "center" : "right"}
      customClass="l1-apop l1-relative"
    >
      {apopComponent}
      <Toast
        showToast={openToast.showToast}
        text={openToast.text}
        type={openToast.type}
        position="top-center"
        width={window.innerWidth < 768 ? "100" : "25"}
        timer={5000}
        onClose={() =>
          setOpenToast({
            showToast: false,
            text: "",
            type: "",
          })
        }
      />
    </Modal>
  );
};

export const Apop = React.memo(ApopComponent);
